import tenantTheme from '@theme';
import { v2Color } from '@theme/v2';
import { Typography } from 'antd';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Flex, Image, TextWithIcon } from '../../common';

export const getInitials = (name) => {
  if (!name || typeof name !== 'string') return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

export const toSafeUserIdString = (id) => {
  if (id == null) return null;
  if (typeof id === 'number' && Number.isFinite(id)) return String(id);
  if (typeof id === 'string' && id.trim() !== '') return id;
  return null;
};

/** Fills parent with no letterboxing; kills inline-image baseline gap */
export const coverImageInBox = {
  wrapperStyle: { width: '100%', height: '100%', display: 'block', lineHeight: 0 },
  style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block', verticalAlign: 'top' },
};

const assigneeRadioOuterStyle = (selected) => ({
  width: 18,
  height: 18,
  borderRadius: '50%',
  border: `2px solid ${selected ? tenantTheme['primary-color'] : '#c4c4c4'}`,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

/**
 * Radio-card grid of agency users for Change Listing Ownership (single and bulk).
 * `value` / `disabledUserId` are user ids as strings (see toSafeUserIdString).
 */
export const AssigneeRadioGrid = ({ users = [], value, onChange, disabledUserId }) => {
  const { t } = useTranslation();
  const { Text } = Typography;
  const { isMobile } = useSelector((state) => state.app.AppConfig);
  const { user } = useSelector((state) => state.app.loginUser);

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, minmax(0, 1fr))',
        gap: '12px',
        maxHeight: isMobile ? 280 : 320,
        overflowY: 'auto',
        paddingBottom: 4,
      }}
      role="radiogroup"
      aria-label={t('Select the new assignee')}
    >
      {users.filter((u) => toSafeUserIdString(u?.id) != null).map((u) => {
        const uid = toSafeUserIdString(u?.id);
        const isDisabled = disabledUserId != null && uid === disabledUserId;
        const selected = !isDisabled && value === uid;
        return (
          <div
            key={uid}
            role="radio"
            aria-checked={selected}
            aria-disabled={isDisabled}
            tabIndex={isDisabled ? -1 : 0}
            onClick={() => {
              if (isDisabled) return;
              onChange(uid);
            }}
            onKeyDown={(ev) => {
              if (isDisabled) return;
              if (ev.key === 'Enter' || ev.key === ' ') {
                ev.preventDefault();
                onChange(uid);
              }
            }}
            style={{
              display: 'block',
              border: `1px solid ${isDisabled ? '#e8e8e8' : selected ? tenantTheme['primary-color'] : '#e0e0e0'}`,
              borderRadius: 10,
              padding: 12,
              paddingTop: 14,
              cursor: isDisabled ? 'not-allowed' : 'pointer',
              background: isDisabled ? '#f5f5f5' : selected ? v2Color('rgba(0, 97, 105, 0.06)') : '#fff',
              opacity: isDisabled ? 0.65 : 1,
              pointerEvents: isDisabled ? 'none' : 'auto',
              position: 'relative',
              margin: 0,
            }}
          >
            <div style={{ position: 'absolute', top: 10, right: 10 }}>
              <span style={assigneeRadioOuterStyle(selected)}>
                {selected ? (
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: '50%',
                      background: tenantTheme['primary-color'],
                    }}
                  />
                ) : null}
              </span>
            </div>
            <Flex align="flex-start" gap={'12px'} style={{ paddingRight: 26, width: '100%' }}>
              <div
                style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  overflow: 'hidden',
                  flexShrink: 0,
                  background: '#eef1f2',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: 9,
                  lineHeight: 1,
                  color: tenantTheme['primary-text-color'],
                }}
              >
                {u?.profile_image ? <Image src={u.profile_image} {...coverImageInBox} /> : getInitials(u?.name || '')}
              </div>
              <Text strong style={{ flex: 1, minWidth: 0, margin: 0, fontSize: 14, lineHeight: '20px' }}>
                {u?.name || '—'}
                {u?.id === user?.id ? ` ${t('(Me)')}` : ''}
              </Text>
            </Flex>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
                marginTop: 10,
                width: '100%',
              }}
            >
              {!!u?.email && (
                <TextWithIcon
                  icon="MdEmail"
                  gap="6px"
                  iconProps={{ size: 12, color: tenantTheme.gray500 }}
                  value={u.email}
                  textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                />
              )}
              {!!(u?.mobile || u?.phone) && (
                <TextWithIcon
                  icon="MdPhone"
                  gap="6px"
                  iconProps={{ size: 12, color: tenantTheme.gray500 }}
                  value={u.mobile || u.phone}
                  textStyle={{ fontSize: 12, lineHeight: 1.3 }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
